import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useAuth as useOidcAuth } from 'react-oidc-context';
import {
  getUserProfileWithAccessToken,
  getLastAuthErrorType,
  hasValidExtendedToken,
  setExtendedToken,
  setTokenProvider
} from '../services/appAuthService';
import { storeAccessToken } from '../utils/auth0Utils';
import { STORAGE_KEYS } from '../utils/storageKeys';

// Build-time constant — Vite replaces this with the literal string at compile time.
const AUTH_PROVIDER = import.meta.env.VITE_AUTH_PROVIDER || 'auth0';

interface AuthReadinessContextType {
  isAuthReady: boolean;
  isLoading: boolean;
  error: string | null;
  extendedToken: string | null;
}

const AuthReadinessContext = createContext<AuthReadinessContextType>({
  isAuthReady: false,
  isLoading: true,
  error: null,
  extendedToken: null,
});

interface AuthReadinessProviderProps {
  children: ReactNode;
}

// ---------------------------------------------------------------------------
// Auth0 implementation (unchanged from original)
// ---------------------------------------------------------------------------
const Auth0ReadinessProvider: React.FC<AuthReadinessProviderProps> = ({ children }) => {
  const {
    isAuthenticated,
    user,
    getAccessTokenSilently,
    isLoading: auth0Loading,
    loginWithRedirect,
  } = useAuth0();

  const [isAuthReady, setIsAuthReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [extendedToken, setExtendedTokenState] = useState<string | null>(null);

  useEffect(() => {
    const initializeAuth = async () => {
      if (auth0Loading) return;

      const params = new URLSearchParams(window.location.search);
      if (
        window.location.pathname === '/invite' ||
        window.location.pathname.startsWith('/workflow-approval/') ||
        (params.has('invitation') && params.has('organization'))
      ) {
        setIsAuthReady(true);
        setIsLoading(false);
        return;
      }

      if (!isAuthenticated || !user) {
        setIsAuthReady(false);
        setIsLoading(false);
        await loginWithRedirect();
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        sessionStorage.setItem(STORAGE_KEYS.INITIAL_AUTH_PHASE, 'true');
        setTokenProvider(getAccessTokenSilently);

        if (hasValidExtendedToken()) {
          const storedToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
          if (storedToken) {
            setExtendedTokenState(storedToken);
            setIsAuthReady(true);
            setIsLoading(false);
            return;
          }
        }

        console.log('🔐 Getting Auth0 token...');
        let initialAccessToken = await getAccessTokenSilently();

        console.log('🔄 Exchanging for extended token...');
        let userProfileResponse = await getUserProfileWithAccessToken(initialAccessToken);

        if (!userProfileResponse?.access_token) {
          // Account was deactivated by an admin - the interceptor already
          // toasted and is forcing a logout. Don't retry, don't error out.
          if (getLastAuthErrorType() === 'account_inactive') {
            setIsAuthReady(false);
            setIsLoading(false);
            return;
          }

          initialAccessToken = await getAccessTokenSilently({ cacheMode: 'off' });
          userProfileResponse = await getUserProfileWithAccessToken(initialAccessToken, true);

          if (!userProfileResponse?.access_token && getLastAuthErrorType() === 'account_inactive') {
            setIsAuthReady(false);
            setIsLoading(false);
            return;
          }
        }

        if (!userProfileResponse?.access_token) {
          throw new Error('Failed to get extended access token from backend');
        }

        const extendedAccessToken = userProfileResponse.access_token;
        storeAccessToken(extendedAccessToken);
        setExtendedToken(extendedAccessToken);
        setExtendedTokenState(extendedAccessToken);

        console.log('✅ Auth ready');

        sessionStorage.removeItem(STORAGE_KEYS.INITIAL_AUTH_PHASE);
        sessionStorage.removeItem(STORAGE_KEYS.AUTH_FAILURE_COUNT);

        setIsAuthReady(true);
      } catch (err: any) {
        console.error('❌ Auth initialization failed:', err);

        if (
          err?.error === 'login_required' ||
          err?.error === 'missing_refresh_token' ||
          err?.message?.includes('Missing Refresh Token')
        ) {
          console.info('🔄 Session invalid → redirecting to login...');
          await loginWithRedirect();
          return;
        }

        setError(err instanceof Error ? err.message : 'Authentication failed');
        setIsAuthReady(false);
        sessionStorage.removeItem(STORAGE_KEYS.INITIAL_AUTH_PHASE);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, [isAuthenticated, user, auth0Loading, getAccessTokenSilently, loginWithRedirect]);

  return (
    <AuthReadinessContext.Provider value={{ isAuthReady, isLoading, error, extendedToken }}>
      {children}
    </AuthReadinessContext.Provider>
  );
};

// ---------------------------------------------------------------------------
// Authentik implementation
// ---------------------------------------------------------------------------
const AuthentikReadinessProvider: React.FC<AuthReadinessProviderProps> = ({ children }) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const auth = useOidcAuth();

  const [isAuthReady, setIsAuthReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [extendedToken, setExtendedTokenState] = useState<string | null>(null);

  useEffect(() => {
    const initializeAuth = async () => {
      // 1. Wait for OIDC SDK to finish loading
      if (auth.isLoading) return;

      // 2. Skip for callback, invite, workflow-approval
      const pathname = window.location.pathname;
      if (
        pathname === '/callback' ||
        pathname === '/invite' ||
        pathname.startsWith('/workflow-approval/')
      ) {
        setIsAuthReady(true);
        setIsLoading(false);
        return;
      }

      // 3. If NOT authenticated, redirect to Authentik login
      if (!auth.isAuthenticated || !auth.user) {
        setIsAuthReady(false);
        setIsLoading(false);
        auth.signinRedirect();
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        // Register signinSilent as the global token provider for background refresh
        setTokenProvider(async () => {
          try {
            const freshUser = await auth.signinSilent();
            return freshUser?.access_token || auth.user?.access_token || '';
          } catch {
            return auth.user?.access_token || '';
          }
        });

        // Use existing extended token if still valid
        if (hasValidExtendedToken()) {
          const storedToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
          if (storedToken) {
            setExtendedTokenState(storedToken);
            setIsAuthReady(true);
            setIsLoading(false);
            return;
          }
        }

        // Exchange the Authentik access token with the backend
        const authentikToken = auth.user.access_token;
        if (!authentikToken) throw new Error('No Authentik access token');

        let userProfileResponse = await getUserProfileWithAccessToken(authentikToken);

        // Retry once with forced refresh if the first attempt failed
        if (!userProfileResponse?.access_token) {
          userProfileResponse = await getUserProfileWithAccessToken(authentikToken, true);
        }

        if (!userProfileResponse?.access_token) {
          throw new Error('Failed to get extended access token from backend');
        }

        const extendedAccessToken = userProfileResponse.access_token;
        storeAccessToken(extendedAccessToken);
        setExtendedToken(extendedAccessToken);
        setExtendedTokenState(extendedAccessToken);

        setIsAuthReady(true);
      } catch (err: any) {
        console.error('[AuthentikReadinessProvider] Auth initialization failed:', err);
        setError(err instanceof Error ? err.message : 'Authentication failed');
        setIsAuthReady(false);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, [auth.isAuthenticated, auth.user, auth.isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AuthReadinessContext.Provider value={{ isAuthReady, isLoading, error, extendedToken }}>
      {children}
    </AuthReadinessContext.Provider>
  );
};

// ---------------------------------------------------------------------------
// Factory — selects implementation at build time
// ---------------------------------------------------------------------------
export const AuthReadinessProvider: React.FC<AuthReadinessProviderProps> = ({ children }) => {
  if (AUTH_PROVIDER === 'authentik') {
    return <AuthentikReadinessProvider>{children}</AuthentikReadinessProvider>;
  }
  return <Auth0ReadinessProvider>{children}</Auth0ReadinessProvider>;
};

export const useAuthReadiness = () => {
  const context = useContext(AuthReadinessContext);
  if (!context) {
    throw new Error('useAuthReadiness must be used within AuthReadinessProvider');
  }
  return context;
};

export default AuthReadinessContext;
